import { useMutation } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import Visibility from "@mui/icons-material/Visibility";
import { Box, Button, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { xsHeight, mdHeight } from "components/AppBar";
import { CommandButton } from "components/CommandButton";
import { ConfirmDialog } from "components/ConfirmDialog";
import {
  contactRoleChipLabel,
  ContactRoleChipLegend,
  ContactRoleChipList,
} from "components/Contacts/ContactRoleChip";
import { EditFacilityContactDialog } from "components/Contacts/EditFacilityContactDialog";
import { IssueMessageCell } from "components/DataGrid";
import { IssueCount } from "components/IssueCount";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { SectionComplianceIssuesButton } from "components/SectionComplianceIssuesButton";
import { tabsOffset } from "components/TabLinks";
import { BILLING_CONTACT_PERMISSION_ERROR_CODE } from "encamp-shared/src/constants/permissionsTypes";
import { rolesIncludeLockedOwnerOperator } from "encamp-shared/src/utils/contactReportRole";
import { getFullName } from "encamp-shared/src/utils/name";
import { gql } from "generated-graphql";
import {
  ContactReportRole,
  FacilityContactsSearchV2Query,
  GetPersonQuery,
  Permission,
} from "generated-graphql/graphql";
import { useAuthorization, useCanEditFacilityBillingContact } from "hooks/useAuthorization";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { formatPhoneNumberAndType } from "util/phoneNumbers";
import { getMessageForErrorCodes } from "utils/errorUtils";

import {
  GET_FACILITY_CONTACTS_FOR_PROFILE_V2,
  useContactIssues,
  useFacilityContacts,
} from "../useFacility";
import { buildSuggestionRows, IssueRow } from "./facilityContactsSuggestions";

export const FACILITY_CONTACTS_SECTION_ID = "facility-contacts";
export const FACILITY_CONTACTS_SECTION_ICON = "📇";

const FACILITY_CONTACTS_SEARCH = gql(`
  query FacilityContactsSearchV2(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    facilityContactsSearch(
      search: $search
      page: $page
      pageSize: $pageSize
      sort: $sort
    ) {
      items {
        id
        reportingRoles
        isPrimaryContact
        person {
          id
          first
          last
          email
          isEncamp
          phones {
            type
            number
          }
        }
      }
      count
    }
  }
`);

const UPSERT_FACILITY_CONTACT = gql(`
  mutation UpsertFacilityContactProfileV2($data: FacilityContactInput!) {
    upsertFacilityContact(data: $data) {
      id
    }
  }
`);

const DELETE_FACILITY_CONTACT = gql(`
  mutation DeleteFacilityContactProfileV2($facilityContactId: ID!) {
    deleteFacilityContact(facilityContactId: $facilityContactId)
  }
`);

const CONTACTS_REFETCH_QUERIES = [FACILITY_CONTACTS_SEARCH, GET_FACILITY_CONTACTS_FOR_PROFILE_V2];

type SearchRow = FacilityContactsSearchV2Query["facilityContactsSearch"]["items"][number];

type DataGridRow = SearchRow | IssueRow;

const isIssueRow = (row: DataGridRow): row is IssueRow => "__issueRow" in row;

export const FacilityContactsSection: React.FC = () => {
  const theme = useTheme();
  const alerts = useAlerts();
  const { tenantId = "", facilityId = "" } = useParams<{
    tenantId: string;
    facilityId: string;
  }>();

  const sectionIssues = useContactIssues();
  const { data: facilityContactsData } = useFacilityContacts(facilityId);
  const fullContacts = useMemo(
    () => facilityContactsData?.facility?.contacts ?? [],
    [facilityContactsData]
  );
  const allIssues = useMemo(
    () => facilityContactsData?.facility?.issues ?? [],
    [facilityContactsData]
  );

  const getRowIssueCount = useCallback(
    (row: SearchRow) =>
      allIssues.filter(
        (issue) =>
          (issue.modelId === row.person.id && issue.modelName === "Person") ||
          (issue.modelName === "FacilityContact" && issue.modelId === row.id)
      ).length,
    [allIssues]
  );

  const { hasPermissionForFacility } = useAuthorization();
  const canWriteContacts = hasPermissionForFacility(facilityId, [Permission.WriteFacilityContact]);
  const { isStaff, isEmulating } = useCurrentUser();
  // Only a real (non-emulating) staff user can edit/delete a locked owner/operator.
  const ownerOperatorLockedForUser =
    (facilityContactsData?.facility?.isOwnerOperatorLocked ?? false) && !(isStaff && !isEmulating);
  const isLockedOwnerOperatorRow = useCallback(
    (row: SearchRow) =>
      ownerOperatorLockedForUser && rolesIncludeLockedOwnerOperator(row.reportingRoles),
    [ownerOperatorLockedForUser]
  );
  // Without the billing permission, contacts holding the billing role stay
  // viewable but cannot be edited or deleted (their person is the billing
  // address on reports).
  const canEditBillingContact = useCanEditFacilityBillingContact(facilityId);
  const isLockedBillingRow = useCallback(
    (row: SearchRow) =>
      !canEditBillingContact && row.reportingRoles.includes(ContactReportRole.EpcraBilling),
    [canEditBillingContact]
  );

  const suggestionRows = useMemo(
    () => buildSuggestionRows(allIssues, canWriteContacts),
    [allIssues, canWriteContacts]
  );

  const [upsertFacilityContact, { loading: savingContact }] = useMutation(UPSERT_FACILITY_CONTACT, {
    refetchQueries: CONTACTS_REFETCH_QUERIES,
  });
  const [deleteFacilityContact, { loading: deletingContact }] = useMutation(
    DELETE_FACILITY_CONTACT,
    { refetchQueries: CONTACTS_REFETCH_QUERIES }
  );

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editRowId, setEditRowId] = useState<string | null>(null);
  const [addingContact, setAddingContact] = useState<ContactReportRole | undefined>(undefined);
  const [rowToDelete, setRowToDelete] = useState<SearchRow | null>(null);
  const [isFiltering, setIsFiltering] = useState(false);

  const defaultSearch = `tenantId:${tenantId} facilityId:${facilityId}`;
  const handleSearchChanged = useCallback(
    (search: string) => {
      setIsFiltering(search.trim() !== defaultSearch.trim());
    },
    [defaultSearch]
  );

  const editRow = useMemo(
    () => (editRowId ? (fullContacts.find((c) => c.id === editRowId) ?? null) : null),
    [editRowId, fullContacts]
  );

  const editRowIssues = useMemo(() => {
    if (!editRow) return [];
    return allIssues.filter(
      (issue) =>
        (issue.modelId === editRow.person.id && issue.modelName === "Person") ||
        (issue.modelName === "FacilityContact" && issue.modelId === editRow.id)
    );
  }, [editRow, allIssues]);

  const openAddDialog = useCallback((role?: ContactReportRole) => {
    setEditRowId(null);
    setAddingContact(role);
    setIsEditDialogOpen(true);
  }, []);

  const openEditDialog = useCallback((row: SearchRow) => {
    setEditRowId(row.id);
    setAddingContact(undefined);
    setIsEditDialogOpen(true);
  }, []);

  const closeEditDialog = useCallback(() => {
    setIsEditDialogOpen(false);
    setEditRowId(null);
    setAddingContact(undefined);
  }, []);

  const upsert = useCallback(
    async (
      person: GetPersonQuery["person"],
      roles: ContactReportRole[],
      managedReportingRoles: ContactReportRole[],
      isPrimaryContact: boolean | undefined
    ) => {
      if (!person) return;
      await upsertFacilityContact({
        variables: {
          data: {
            person: {
              id: person.id.length ? person.id : undefined,
              tenantId,
              first: person.first,
              last: person.last,
              email: person.email,
              title: person.title,
              company: person.company,
              phones: person.phones?.map((p) => ({ number: p.number, type: p.type })),
              attentionTo: person.attentionTo,
              streetAddress1: person.streetAddress1,
              streetAddress2: person.streetAddress2,
              city: person.city,
              state: person.state,
              zip: person.zip,
              country: person.country,
              tankOwnerType: person.tankOwnerType,
              dbNumber: person.dbNumber,
            },
            facilityId,
            reportingRoles: roles,
            managedReportingRoles,
            isPrimaryContact,
          },
        },
        onCompleted: () => alerts.success("Contact saved successfully!"),
        onError: (err) =>
          alerts.error(
            getMessageForErrorCodes(err, [BILLING_CONTACT_PERMISSION_ERROR_CODE]) ??
              "An error occurred while saving the contact."
          ),
      });
    },
    [upsertFacilityContact, tenantId, facilityId, alerts]
  );

  const runDelete = useCallback(
    async (row: SearchRow) => {
      await deleteFacilityContact({
        variables: { facilityContactId: row.id },
        onCompleted: () => alerts.success("Contact removed successfully!"),
        onError: (err) => alerts.error(err.message),
      });
    },
    [deleteFacilityContact, alerts]
  );

  const columns: GridColDef<DataGridRow>[] = useMemo(
    () => [
      {
        field: "reportingRoles",
        headerName: "Role",
        flex: 1,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(ContactReportRole),
        enumPresentationFunction: (enumValue: string) =>
          contactRoleChipLabel(enumValue as ContactReportRole),
        colSpan: (_, row) => (isIssueRow(row) ? 5 : undefined),
        valueGetter: (_, row) =>
          isIssueRow(row)
            ? ""
            : (row.reportingRoles?.map((role) => contactRoleChipLabel(role)).join(", ") ?? ""),
        renderCell: ({ row }) => {
          if (isIssueRow(row)) {
            return (
              <Box
                display="flex"
                flexDirection="column"
                height="100%"
                alignItems="start"
                justifyContent="center"
              >
                <IssueMessageCell issue={row.issue} />
              </Box>
            );
          }
          return <ContactRoleChipList roles={row.reportingRoles} />;
        },
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        valueGetter: (_, row) => (isIssueRow(row) ? "" : getFullName(row.person)),
      },
      {
        field: "phone",
        headerName: "Phone",
        flex: 1.25,
        sortable: false,
        valueGetter: (_, row) =>
          isIssueRow(row)
            ? ""
            : (row.person.phones?.map((phone) => formatPhoneNumberAndType(phone)).join(", ") ?? ""),
        renderCell: ({ row }) => {
          if (isIssueRow(row)) return null;
          return (
            <Box sx={{ display: "flex", flexDirection: "column", py: 1 }}>
              {row.person.phones?.map((phone, i) => (
                <Typography key={i} variant="body2">
                  {formatPhoneNumberAndType(phone)}
                </Typography>
              ))}
            </Box>
          );
        },
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1.5,
        valueGetter: (_, row) => (isIssueRow(row) ? "" : (row.person.email ?? "")),
      },
      {
        field: "issues",
        headerName: "Issues",
        sortable: false,
        flex: 0.5,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) =>
          isIssueRow(row) ? null : <IssueCount issueCount={getRowIssueCount(row)} />,
      },
      {
        field: "actions",
        type: "actions",
        align: "right",
        flex: 0.5,
        minWidth: 120,
        getActions: ({ row }) => {
          if (isIssueRow(row)) {
            return [
              <Button
                key="add-suggestion"
                size="small"
                startIcon={<Add fontSize="small" />}
                onClick={() => openAddDialog(row.requiredReportingRole)}
              >
                Add Contact
              </Button>,
            ];
          }
          if (!canWriteContacts || row.person.isEncamp) return [];
          // Locked owner/operator and locked billing contacts stay viewable but
          // not editable or deletable.
          const lockedRow = isLockedOwnerOperatorRow(row) || isLockedBillingRow(row);
          return [
            <Tooltip title={lockedRow ? "View Contact" : "Edit Contact"} key="edit">
              <GridActionsCellItem
                label={lockedRow ? "View" : "Edit"}
                icon={lockedRow ? <Visibility /> : <Edit />}
                onClick={() => openEditDialog(row)}
              />
            </Tooltip>,
            ...(lockedRow
              ? []
              : [
                  <Tooltip title="Delete Contact" key="delete">
                    <GridActionsCellItem
                      label="Delete"
                      icon={<Delete />}
                      onClick={() => setRowToDelete(row)}
                    />
                  </Tooltip>,
                ]),
          ];
        },
      },
    ],
    [
      canWriteContacts,
      getRowIssueCount,
      openAddDialog,
      openEditDialog,
      isLockedOwnerOperatorRow,
      isLockedBillingRow,
    ]
  );

  const commandButtons = useMemo(
    () =>
      canWriteContacts
        ? [
            <CommandButton key="add" variant="contained" onClick={() => openAddDialog()}>
              Add Contact
            </CommandButton>,
          ]
        : [],
    [canWriteContacts, openAddDialog]
  );

  return (
    <Box
      id={FACILITY_CONTACTS_SECTION_ID}
      sx={{
        scrollMarginTop: {
          xs: theme.spacing(xsHeight + tabsOffset + 11),
          md: theme.spacing(mdHeight + tabsOffset + 11),
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={5}>
        <Typography variant="h6">{FACILITY_CONTACTS_SECTION_ICON} Facility Contacts</Typography>
        <SectionComplianceIssuesButton
          issues={sectionIssues}
          sectionHeader={`${FACILITY_CONTACTS_SECTION_ICON} Facility Contacts`}
        />
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Showing all current facility contacts.
      </Typography>

      <OmnisearchDataGrid
        withPadding={false}
        persistenceKey={`facility-contacts-v2-${facilityId}`}
        isURLDriven
        showManageColumns={false}
        columns={columns}
        dataQuery={FACILITY_CONTACTS_SEARCH}
        defaultSearch={defaultSearch}
        onSearchChanged={handleSearchChanged}
        getItems={(data): DataGridRow[] => [
          ...data.facilityContactsSearch.items,
          ...(isFiltering ? [] : suggestionRows),
        ]}
        getCount={(data) => data.facilityContactsSearch.count}
        initialSortModel={[{ field: "name", sort: "asc" }]}
        noDataMessage="No contacts for this facility."
        excludeFilterColumns={["phone", "reportingRoles", "issues"]}
        additionalFilterColumns={[
          {
            key: "reportingRole",
            header: "Role",
            filterKeyType: "enumAutocomplete",
            enumValues: Object.values(ContactReportRole),
            enumPresentationFunction: (enumValue) =>
              contactRoleChipLabel(enumValue as ContactReportRole),
          },
        ]}
        commandButtons={commandButtons}
        rowHeight="auto"
        disableRowSelectionOnClick
        onRowClick={
          canWriteContacts
            ? ({ row }) => {
                if (isIssueRow(row)) {
                  openAddDialog(row.requiredReportingRole);
                  return;
                }
                if (!row.person.isEncamp) openEditDialog(row);
              }
            : undefined
        }
      />

      <ContactRoleChipLegend />

      {isEditDialogOpen && (
        <EditFacilityContactDialog
          onClose={closeEditDialog}
          onSave={async (roles, managedReportingRoles, person, isPrimaryContact) => {
            await upsert(person, roles, managedReportingRoles, isPrimaryContact);
            closeEditDialog();
          }}
          data={editRow}
          contacts={fullContacts}
          addingContact={addingContact}
          setEditRow={(row) => setEditRowId(row?.id ?? null)}
          initialIssues={editRowIssues}
          facilityId={facilityId}
          saving={savingContact}
        />
      )}

      {rowToDelete && (
        <ConfirmDialog
          open
          loading={deletingContact}
          onClose={() => setRowToDelete(null)}
          onConfirm={async () => {
            await runDelete(rowToDelete);
            setRowToDelete(null);
          }}
          header="Are you sure you want to delete this contact?"
          resources={[
            {
              resourceType: "Contact",
              resourceName: getFullName(rowToDelete.person),
            },
          ]}
        />
      )}
    </Box>
  );
};
